/**
 * MicroCMS リッチテキスト内の YouTube 埋め込みを facade 化するための純粋ロジック。
 *
 * Vue/Nuxt に一切依存しない（core/ 配下）。SSR（Node）・クライアント双方で同一に動作し、
 * Vitest でテストする。詳細は openspec/changes/youtube-facade-embed/design/wabunka-consumer.md を参照。
 */

/** リッチテキストを描画単位に分割したセグメント。 */
export type RichTextSegment = { kind: 'html'; html: string } | { kind: 'youtube'; videoId: string };

export type YouTubeThumbnailQuality = 'maxresdefault' | 'hqdefault';

/** YouTube videoId として許可する形式（11 文字のホワイトリスト）。 */
const VIDEO_ID_PATTERN = '[a-zA-Z0-9_-]{11}';

/** src が YouTube / youtube-nocookie の embed URL であるか（ID の妥当性は問わない）。 */
const YOUTUBE_EMBED_SRC = /^https?:\/\/(?:www\.)?(?:youtube\.com|youtube-nocookie\.com)\/embed\//i;

/** embed URL から 11 文字の videoId を厳格に抽出する。 */
const VIDEO_ID_FROM_SRC = new RegExp(`^https?://(?:www\\.)?(?:youtube\\.com|youtube-nocookie\\.com)/embed/(${VIDEO_ID_PATTERN})(?:[/?#]|$)`, 'i');

/**
 * responsive ラッパー `<div ...>` が embed iframe を内包するブロックを検出する。
 * - 属性順序・改行・複合 style・余分な属性に頑健にするため、タグ内は `[^>]*`、
 *   タグ間の空白は `\s*` で吸収する（`/i` で大文字小文字無視）。
 * - src は属性順を問わず捕捉する（src の前に任意の属性を許可）。ダブル/シングルクオート両対応。
 *
 * 既知の限界（MicroCMS の実出力＝単一 responsive ラッパー＋iframe では発生しない）:
 * - 外側にもう一段ラッパー div がある入れ子構造では内側のみ一致し、外側タグが分離する。
 * - div ラッパーを持たない bare な iframe や、iframe の前に兄弟要素がある div は検出しない。
 *   これらは MicroCMS が生成しないため許容。完全対応には DOM パーサが要るが、本パーサは
 *   SSR（DOMParser 非提供）でも動くため正規表現方式を維持する。挙動は spec のテストで明文化。
 */
const WRAPPED_IFRAME_BLOCK = /<div\b[^>]*>\s*<iframe\b[^>]*?\ssrc=["']([^"']*)["'][^>]*>\s*<\/iframe>\s*<\/div>/gi;

/**
 * src が YouTube embed（youtube.com / youtube-nocookie.com の /embed/）かどうか。
 * ID が不正でも true（「YouTube 埋め込みと判定できるが videoId 抽出不可」を識別するため）。
 */
export const isYouTubeEmbedSrc = (src: string): boolean => YOUTUBE_EMBED_SRC.test(src);

/**
 * embed の src から videoId を抽出する。YouTube embed でない、または 11 文字 ID でない場合は null。
 */
export const extractVideoId = (src: string): string | null => {
  const matched = src.match(VIDEO_ID_FROM_SRC);
  return matched ? matched[1] : null;
};

/** YouTube サムネイル画像 URL を構築する。 */
export const buildThumbnailUrl = (quality: YouTubeThumbnailQuality, videoId: string): string => `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;

/**
 * 再生用の youtube-nocookie 埋め込み URL を構築する。
 * - `playsinline=1`: iOS で全画面強制を避けインライン再生。
 * - `muted=true`: iOS は autoplay 制限により未ミュートだと初回タップで再生されない（プレーヤーが
 *   一時停止表示になり 2 タップ必要）。iOS では `mute=1` を付けて 1 タップ再生させる用途。
 */
export const buildEmbedSrc = (videoId: string, opts?: { muted?: boolean }): string => {
  const params = ['autoplay=1', 'rel=0', 'playsinline=1'];
  if (opts?.muted) params.push('mute=1');
  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.join('&')}`;
};

/**
 * リッチテキスト HTML を「HTML チャンク」と「YouTube 埋め込み」のセグメント列に分割する。
 *
 * - 検出キーは iframe の src が YouTube embed であること（ラッパーの style 文字列には依存しない）。
 * - YouTube 埋め込みは、それを内包する responsive ラッパー `<div>` ごと 1 セグメントとして取り込む
 *   （空のラッパーが残らないようブロック全体を消費する）。
 * - YouTube 以外の iframe（地図等）は分割対象にせず、そのまま HTML チャンクに残す。
 * - YouTube embed と判定できるが videoId を抽出できないブロックは、除去して何も描画しない
 *   （壊れた YouTube iframe を v-html でそのまま出力しない）。
 */
export const segmentizeHtml = (html: string | null | undefined): RichTextSegment[] => {
  // MicroCMS のリッチテキストフィールドが空の体験では html が undefined で渡る経路があり、
  // ガードしないと html.length 参照で TypeError → SSR 500 になる。空入力は空セグメントで返す。
  if (html == null || html === '') return [];

  const segments: RichTextSegment[] = [];
  // gi フラグの正規表現を使い回すと lastIndex が共有されるため、毎回新しいインスタンスを生成する。
  const re = new RegExp(WRAPPED_IFRAME_BLOCK.source, 'gi');
  let chunkStart = 0;
  let match: RegExpExecArray | null;

  const pushHtml = (from: number, to: number) => {
    if (to > from) {
      const chunk = html.slice(from, to);
      if (chunk.length > 0) segments.push({ kind: 'html', html: chunk });
    }
  };

  while ((match = re.exec(html)) !== null) {
    const src = match[1];

    // YouTube 以外の iframe ブロックは分割境界にせず、HTML チャンクに残す。
    if (!isYouTubeEmbedSrc(src)) continue;

    // 直前までの HTML を確定（YouTube 以外の iframe ブロックはここに含まれて通過する）。
    pushHtml(chunkStart, match.index);

    const videoId = extractVideoId(src);
    if (videoId) {
      segments.push({ kind: 'youtube', videoId });
    }
    // videoId 抽出不可（YouTube embed だが不正 ID）の場合はブロックを消費し、何も push しない。

    chunkStart = match.index + match[0].length;
  }

  // 残りの HTML を確定。
  pushHtml(chunkStart, html.length);

  return segments;
};
